import React, { useState, useEffect } from 'react';
import { Label } from './ui/label';

import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { HardHat, Wrench, ShoppingBag, Utensils, Scissors, Gamepad2, Factory, Hotel, Leaf, Lock, X, Check, Info } from 'lucide-react';

// ============== TOUTES LES CATÉGORIES ET MÉTIERS ==============

const CATEGORIES = {
  btp: {
    id: 'btp',
    name: 'Activités du BTP',
    icon: HardHat,
    color: 'bg-orange-600',
    borderColor: 'border-orange-200',
    bgColor: 'bg-orange-50',
    metiers: {
      "Gros Œuvre": [
        "Maçonnerie générale",
        "Béton armé / coffrage / ferraillage",
        "Charpente bois",
        "Couverture / étanchéité",
        "Fondations / dallage",
        "Démolition / terrassement bâtiment"
      ],
      "Second Œuvre": [
        "Cloisons / placo / plâtrerie",
        "Menuiseries intérieures",
        "Menuiseries extérieures",
        "Carrelage / faïence",
        "Peinture / revêtements",
        "Électricité logements",
        "Plomberie / sanitaire",
        "Isolation thermique / acoustique",
        "Serrurerie légère"
      ],
      "Travaux Publics": [
        "Terrassement généraux",
        "VRD - Réseaux divers",
        "Voirie / enrobés",
        "Aménagements urbains",
        "Réseaux eau potable / assainissement",
        "Ouvrages hydrauliques"
      ]
    }
  },
  services: {
    id: 'services',
    name: 'Services',
    icon: Wrench,
    color: 'bg-blue-700',
    borderColor: 'border-blue-200',
    bgColor: 'bg-blue-50',
    metiers: {
      "Services aux entreprises": [
        "Conseil en gestion",
        "Comptabilité",
        "Services informatiques",
        "Communication / Marketing",
        "Formation professionnelle"
      ],
      "Services aux particuliers": [
        "Aide à domicile",
        "Jardinage / Espaces verts",
        "Nettoyage / Entretien",
        "Garde d'enfants",
        "Assistance administrative"
      ],
      "Services techniques": [
        "Maintenance industrielle",
        "Réparation automobile",
        "Dépannage informatique",
        "Installation équipements"
      ]
    }
  },
  commerce: {
    id: 'commerce',
    name: 'Commerce',
    icon: ShoppingBag,
    color: 'bg-green-700',
    borderColor: 'border-green-200',
    bgColor: 'bg-green-50',
    metiers: {
      "Commerce de détail": [
        "Alimentation générale",
        "Boulangerie / Pâtisserie",
        "Boucherie / Charcuterie",
        "Fruits et légumes",
        "Produits bio / locaux"
      ],
      "Commerce spécialisé": [
        "Prêt-à-porter",
        "Chaussures / Maroquinerie",
        "Articles de sport",
        "Décoration / Ameublement",
        "Librairie / Papeterie"
      ],
      "Commerce ambulant": [
        "Marchés alimentaires",
        "Marchés non alimentaires",
        "Food truck",
        "Vente itinérante"
      ]
    }
  },
  metiers_bouche: {
    id: 'metiers_bouche',
    name: 'Métiers de Bouche',
    icon: Utensils,
    color: 'bg-red-600',
    borderColor: 'border-red-200',
    bgColor: 'bg-red-50',
    metiers: {
      "Restauration": [
        "Restaurant traditionnel",
        "Restauration rapide",
        "Traiteur",
        "Pizzeria",
        "Crêperie"
      ],
      "Boulangerie / Pâtisserie": [
        "Boulanger",
        "Pâtissier",
        "Chocolatier",
        "Glacier",
        "Confiseur"
      ],
      "Boucherie / Charcuterie": [
        "Boucher",
        "Charcutier",
        "Tripier",
        "Volailler"
      ]
    }
  },
  metiers_mode: {
    id: 'metiers_mode',
    name: 'Métiers de la Mode',
    icon: Scissors,
    color: 'bg-pink-600',
    borderColor: 'border-pink-200',
    bgColor: 'bg-pink-50',
    metiers: {
      "Confection": [
        "Couturier / Couturière",
        "Tailleur",
        "Retoucheur",
        "Modéliste",
        "Styliste"
      ],
      "Accessoires": [
        "Maroquinier",
        "Chapelier",
        "Bijoutier fantaisie",
        "Créateur d'accessoires"
      ],
      "Beauté": [
        "Coiffeur",
        "Esthéticien(ne)",
        "Maquilleur",
        "Prothésiste ongulaire"
      ]
    }
  },
  loisirs: {
    id: 'loisirs',
    name: 'Activités de Loisirs',
    icon: Gamepad2,
    color: 'bg-purple-700',
    borderColor: 'border-purple-200',
    bgColor: 'bg-purple-50',
    metiers: {
      "Sport & Bien-être": [
        "Coach sportif",
        "Professeur de yoga",
        "Moniteur de fitness",
        "Kinésiologue"
      ],
      "Arts & Culture": [
        "Professeur de musique",
        "Artiste peintre",
        "Photographe",
        "Animateur culturel"
      ],
      "Tourisme & Loisirs": [
        "Guide touristique",
        "Animateur de loisirs",
        "Organisateur d'événements",
        "Moniteur d'activités"
      ]
    }
  },
  agrotransformees: {
    id: 'agrotransformees',
    name: 'Activités Agro-transformées',
    icon: Leaf,
    color: 'bg-emerald-700',
    borderColor: 'border-emerald-200',
    bgColor: 'bg-emerald-50',
    metiers: {
      "Production agricole": [
        "Maraîcher",
        "Arboriculteur",
        "Éleveur",
        "Apiculteur",
        "Viticulteur"
      ],
      "Transformation": [
        "Conserverie",
        "Fromagerie",
        "Distillerie",
        "Huilerie",
        "Confiturerie"
      ],
      "Vente directe": [
        "Vente à la ferme",
        "Marché de producteurs",
        "AMAP / Paniers",
        "E-commerce agricole"
      ]
    }
  },
  industrielles: {
    id: 'industrielles',
    name: 'Activités Industrielles',
    icon: Factory,
    color: 'bg-slate-600',
    borderColor: 'border-slate-200',
    bgColor: 'bg-slate-50',
    metiers: {
      "Fabrication": [
        "Métallurgie",
        "Plasturgie",
        "Menuiserie industrielle",
        "Imprimerie",
        "Électronique"
      ],
      "Maintenance": [
        "Maintenance industrielle",
        "Maintenance machines",
        "SAV industriel"
      ],
      "Logistique": [
        "Transport de marchandises",
        "Entreposage",
        "Manutention"
      ]
    }
  },
  hebergement: {
    id: 'hebergement',
    name: 'Hébergement Outremer',
    icon: Hotel,
    color: 'bg-cyan-700',
    borderColor: 'border-cyan-200',
    bgColor: 'bg-cyan-50',
    metiers: {
      "Hôtellerie": [
        "Hôtel",
        "Résidence de tourisme",
        "Village vacances",
        "Auberge de jeunesse"
      ],
      "Locations": [
        "Gîte rural",
        "Meublé de tourisme",
        "Chambre d'hôtes",
        "Location saisonnière"
      ],
      "Camping": [
        "Camping",
        "Aire naturelle",
        "Glamping / Écolodge"
      ]
    }
  }
};

const UnifiedActivitySelector = ({ value, onChange }) => {
  // value = { category: string, metiers: string[] }
  const [selectedCategory, setSelectedCategory] = useState(value?.category || null);
  const [selectedMetiers, setSelectedMetiers] = useState(value?.metiers || []);
  const [isLocked, setIsLocked] = useState(false);

  // Synchroniser avec les props
  useEffect(() => {
    if (value?.category) {
      setSelectedCategory(value.category);
      setSelectedMetiers(value.metiers || []);
      setIsLocked(true);
    }
  }, [value]);

  // Notifier le parent des changements
  useEffect(() => {
    if (selectedCategory && selectedMetiers.length > 0) {
      onChange({
        category: selectedCategory,
        categoryName: CATEGORIES[selectedCategory]?.name,
        metiers: selectedMetiers
      });
    } else if (!selectedCategory) {
      onChange(null);
    }
  }, [selectedCategory, selectedMetiers]);

  const handleCategorySelect = (categoryId) => {
    if (isLocked && categoryId !== selectedCategory) {
      return; // Catégorie verrouillée
    }
    setSelectedCategory(categoryId);
    setSelectedMetiers([]);
    if (categoryId) {
      setIsLocked(true); // Verrouiller après sélection
    }
  };

  const handleMetierToggle = (metier) => {
    setSelectedMetiers(prev => {
      if (prev.includes(metier)) {
        return prev.filter(m => m !== metier);
      } else {
        return [...prev, metier];
      }
    });
  };

  const handleReset = () => {
    setSelectedCategory(null);
    setSelectedMetiers([]);
    setIsLocked(false);
    onChange(null);
  };

  const category = selectedCategory ? CATEGORIES[selectedCategory] : null;
  const CategoryIcon = category?.icon || HardHat;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold flex items-center gap-2">
          <HardHat className="h-5 w-5 text-slate-600" />
          Catégorie d'activité professionnelle
        </Label>
        {isLocked && (
          <Button variant="outline" size="sm" onClick={handleReset} className="text-red-600 hover:text-red-700">
            <X className="h-4 w-4 mr-1" />
            Réinitialiser
          </Button>
        )}
      </div>

      {/* Message d'information */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800 text-sm">
          Sélectionnez <strong>une catégorie</strong> puis <strong>un ou plusieurs métiers</strong> correspondant à votre activité. 
          Une fois la catégorie choisie, vous ne pourrez pas en sélectionner une autre.
        </AlertDescription>
      </Alert>

      {/* Sélection de catégorie */}
      {!selectedCategory ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.values(CATEGORIES).map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => handleCategorySelect(cat.id)}
                className={`p-4 rounded-lg border-2 transition-all hover:shadow-md text-left
                  ${cat.borderColor} ${cat.bgColor} hover:border-opacity-100`}
              >
                <Icon className={`h-6 w-6 mb-2 ${cat.color.replace('bg-', 'text-')}`} />
                <p className="font-medium text-sm text-slate-800">{cat.name}</p>
              </button>
            );
          })}
        </div>
      ) : (
        /* Catégorie sélectionnée - Afficher avec verrou */
        <Card className={`${category.borderColor} ${category.bgColor} border-2`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${category.color}`}>
                  <CategoryIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg">{category.name}</CardTitle>
                  <p className="text-sm text-slate-600">
                    {selectedMetiers.length} métier(s) sélectionné(s)
                  </p>
                </div>
              </div>
              <Badge className="flex items-center gap-1 bg-slate-600 text-white font-semibold">
                <Lock className="h-3 w-3" />
                Verrouillé
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            {/* Afficher les métiers sélectionnés */}
            {selectedMetiers.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-slate-200">
                {selectedMetiers.map((metier) => (
                  <Badge 
                    key={metier} 
                    className={`${category.color} text-white cursor-pointer hover:opacity-80`}
                    onClick={() => handleMetierToggle(metier)}
                  >
                    {metier}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                ))}
              </div>
            )}

            {/* Liste des métiers par sous-catégorie */}
            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {Object.entries(category.metiers).map(([sousCategorie, metiers]) => (
                <div key={sousCategorie} className="space-y-2">
                  <p className="font-semibold text-sm text-slate-700 sticky top-0 bg-white py-1">
                    {sousCategorie}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {metiers.map((metier) => {
                      const isSelected = selectedMetiers.includes(metier);
                      return (
                        <label
                          key={metier}
                          className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors
                            ${isSelected 
                              ? `${category.bgColor} ${category.borderColor} border` 
                              : 'hover:bg-slate-100 border border-transparent'
                            }`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleMetierToggle(metier)}
                            className={isSelected ? category.color.replace('bg-', 'border-') : ''}
                          />
                          <span className={`text-sm ${isSelected ? 'font-medium' : ''}`}>
                            {metier}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Récapitulatif */}
      {selectedCategory && selectedMetiers.length > 0 && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-800">
            <Check className="h-5 w-5" />
            <span className="font-medium">
              {selectedMetiers.length} métier(s) sélectionné(s) dans "{category.name}"
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedActivitySelector;
